import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import DateInputBR from "@/components/DateInputBR";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Clock, Plus, Upload, FileDown, AlertCircle, History, Trash2,
  CheckCircle2, XCircle, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const HOUR_TYPES = {
  he_50: "HE 50%", he_60: "HE 60%", he_75: "HE 75%", he_100: "HE 100%",
  hora_noturna: "Hora noturna", adicional_noturno: "Adicional noturno",
  dsr_he: "DSR sobre HE",
  banco_pos: "Banco de Horas (+)", banco_neg: "Banco de Horas (-)",
  banco_pago: "Banco de Horas pago", banco_gozado: "Banco de Horas gozado",
  compensacao: "Compensação", sobreaviso: "Sobreaviso",
  prontidao: "Prontidão", in_itinere: "Hora in itinere", outros: "Outros",
};

const TYPE_TONE = {
  he_50: "bg-amber-100 text-amber-800",
  he_60: "bg-amber-200 text-amber-900",
  he_75: "bg-orange-100 text-orange-800",
  he_100: "bg-rose-100 text-rose-800",
  banco_pos: "bg-emerald-100 text-emerald-800",
  banco_neg: "bg-red-100 text-red-800",
  banco_pago: "bg-blue-100 text-blue-800",
  banco_gozado: "bg-cyan-100 text-cyan-800",
  compensacao: "bg-teal-100 text-teal-800",
  sobreaviso: "bg-violet-100 text-violet-800",
  prontidao: "bg-purple-100 text-purple-800",
  hora_noturna: "bg-indigo-100 text-indigo-800",
  adicional_noturno: "bg-indigo-100 text-indigo-800",
  dsr_he: "bg-sky-100 text-sky-800",
  in_itinere: "bg-slate-100 text-slate-800",
  outros: "bg-slate-100 text-slate-800",
};

const currency = (v) => Number(v || 0).toLocaleString("pt-BR",
  { style: "currency", currency: "BRL" });

const emptyForm = {
  matricula: "", data: "", tipo_hora: "he_50", quantidade_horas: 1,
  valor_hora: 0, valor_total: 0, percentual_he: 50,
  funcao: "", centro_custo: "", equipamento_codigo: "", frente_trabalho: "",
  situacao: "ativa", observacao: "",
};

export default function HorasExtrasPage() {
  const { hasRole } = useAuth();
  const canImport = hasRole("master", "rh_admin", "rh_operational");
  const canDelete = hasRole("master", "rh_admin");
  const [tab, setTab] = useState("dashboard");
  const [period, setPeriod] = useState("90d");
  const [dashboard, setDashboard] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [imports, setImports] = useState([]);
  const [openManual, setOpenManual] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [openImport, setOpenImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileInput = useRef(null);

  const loadDashboard = async () => {
    try {
      const params = {};
      const to = new Date();
      const from = new Date();
      if (period === "30d") from.setDate(from.getDate() - 30);
      else if (period === "90d") from.setDate(from.getDate() - 90);
      else if (period === "ytd") from.setMonth(0, 1);
      else if (period === "12m") from.setMonth(from.getMonth() - 12);
      params.date_from = from.toISOString().slice(0, 10);
      params.date_to = to.toISOString().slice(0, 10);
      const { data } = await api.get("/horas-extras/dashboard", { params });
      setDashboard(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadList = async () => {
    try {
      const { data } = await api.get("/horas-extras", { params: { page: 1, page_size: 100 } });
      setList(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadImports = async () => {
    try {
      const { data } = await api.get("/horas-extras/imports");
      setImports(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDashboard(); }, [period]);
  useEffect(() => {
    if (tab === "registros") loadList();
    if (tab === "historico") loadImports();
  }, [tab]);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/horas-extras/template`,
        { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "horas-extras-template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Erro ao baixar modelo"); }
  };

  const onFileSelected = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("rh_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/horas-extras/import/preview`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Erro");
      setImportPreview({ ...data, _filename: file.name });
      setOpenImport(true);
    } catch (e) { toast.error(e.message || "Falha ao processar arquivo"); }
  };

  const commitImport = async () => {
    if (!importPreview?.valid?.length) return;
    try {
      const { data } = await api.post("/horas-extras/import/commit", {
        filename: importPreview._filename,
        rows: importPreview.valid,
      });
      toast.success(`Importação concluída: ${data.inserted} registros`);
      setOpenImport(false);
      setImportPreview(null);
      await loadDashboard();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitManual = async () => {
    if (!form.matricula || !form.data || !form.quantidade_horas) {
      toast.error("Preencha matrícula, data e horas."); return;
    }
    try {
      await api.post("/horas-extras", form);
      toast.success("Lançamento criado");
      setOpenManual(false);
      setForm(emptyForm);
      await loadDashboard();
      if (tab === "registros") await loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const undoImport = async (iid) => {
    if (!window.confirm("Desfazer esta importação? Os registros serão removidos.")) return;
    try {
      await api.delete(`/horas-extras/imports/${iid}`);
      toast.success("Importação desfeita");
      await loadImports();
      await loadDashboard();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const k = dashboard?.kpis || {};
  const r = dashboard?.rankings || {};

  return (
    <div className="space-y-6" data-testid="horas-extras-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Operacional · Horas Extras</div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <Clock className="h-7 w-7 text-[#234935]" /> Horas Extras &amp; Banco de Horas
          </h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Importação Excel, indicadores gerenciais por colaborador, setor, equipamento e centro de custo. Banco de horas integrado.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate}>
            <FileDown className="h-3.5 w-3.5 mr-1" /> Modelo Excel
          </Button>
          {canImport && (
            <>
              <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" hidden
                     onChange={(e) => onFileSelected(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => fileInput.current?.click()} data-testid="he-import-btn">
                <Upload className="h-3.5 w-3.5 mr-1" /> Importar planilha
              </Button>
              <Button onClick={() => { setForm(emptyForm); setOpenManual(true); }}
                      className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="he-new-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Novo lançamento
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="he-tab-dashboard">Painel Gerencial</TabsTrigger>
          <TabsTrigger value="registros" data-testid="he-tab-registros">Registros</TabsTrigger>
          <TabsTrigger value="historico" data-testid="he-tab-historico">Histórico de importações</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4 mt-4">
          <div className="bg-white border border-border rounded-lg p-3 flex items-center gap-2 flex-wrap">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Período</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="ytd">Ano atual</SelectItem>
                <SelectItem value="12m">Últimos 12 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Horas extras" value={`${k.total_horas || 0}h`} sub={`${k.colaboradores_distintos || 0} colaboradores`} accent="ochre" />
            <Kpi label="Custo total" value={currency(k.total_valor)} sub={`Média: ${k.media_horas_por_colab || 0}h/colab`} accent="rose" />
            <Kpi label="Saldo banco de horas" value={`${k.saldo_banco_horas || 0}h`} sub={`+${k.banco_horas_positivo || 0} · -${k.banco_horas_negativo || 0}`} accent="emerald" />
            <Kpi label="Setor maior custo" value={k.setor_maior_custo || "—"} sub={`Equip.: ${k.equipamento_maior_custo || "—"}`} accent="slate" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Top 10 colaboradores (horas)">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Matrícula</TableHead><TableHead>Nome</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead className="text-right">Horas</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(r.colaboradores || []).slice(0, 10).map((c, i) => (
                    <TableRow key={`${c.matricula}-${i}`}>
                      <TableCell className="font-mono text-[11.5px]">{c.matricula}</TableCell>
                      <TableCell className="text-[12.5px]">{c.nome}</TableCell>
                      <TableCell className="text-[12.5px] text-muted-foreground">{c.setor}</TableCell>
                      <TableCell className="text-right">{c.horas.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono text-[11.5px]">{currency(c.valor)}</TableCell>
                    </TableRow>
                  ))}
                  {!r.colaboradores?.length && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Sem dados no período</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Section>

            <Section title="Distribuição por tipo de hora">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(r.tipos_hora || []).slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" tick={{ fontSize: 10 }} width={120} tickFormatter={(v) => HOUR_TYPES[v] || v} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="horas" fill="#C5A059" name="Horas" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Top 10 setores (custo)">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(r.setores || []).slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => currency(v)} />
                    <Bar dataKey="valor" fill="#234935" name="Valor" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Top 10 equipamentos (custo)">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Equipamento</TableHead>
                  <TableHead className="text-right">Horas</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(r.equipamentos || []).slice(0, 10).map((e, i) => (
                    <TableRow key={`${e.nome}-${i}`}>
                      <TableCell className="text-[12.5px]">{e.nome}</TableCell>
                      <TableCell className="text-right">{e.horas.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono text-[11.5px]">{currency(e.valor)}</TableCell>
                    </TableRow>
                  ))}
                  {!r.equipamentos?.length && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">Sem dados</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="registros" className="mt-4">
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Colaborador</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Equipamento</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {!list.items?.length ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Nenhum registro</TableCell></TableRow>
                ) : list.items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-mono text-[11.5px]">{it.data}</TableCell>
                    <TableCell className="font-mono text-[11.5px]">{it.matricula}</TableCell>
                    <TableCell className="text-[12.5px]">{it.nome_colaborador}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${TYPE_TONE[it.tipo_hora] || "bg-slate-100"}`}>{HOUR_TYPES[it.tipo_hora] || it.tipo_hora}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{Number(it.quantidade_horas).toFixed(1)}</TableCell>
                    <TableCell className="text-right font-mono text-[11.5px]">{currency(it.valor_total)}</TableCell>
                    <TableCell className="text-[11.5px] text-muted-foreground">{it.equipamento_nome || "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground capitalize">{it.origem_informacao || "—"}</TableCell>
                    <TableCell>
                      {canDelete && (
                        <Button size="icon" variant="ghost" onClick={async () => {
                          if (!window.confirm("Excluir este registro?")) return;
                          await api.delete(`/horas-extras/${it.id}`);
                          toast.success("Excluído");
                          await loadList();
                          await loadDashboard();
                        }}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Arquivo</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Por</TableHead>
                <TableHead className="text-right">Linhas</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {!imports.length ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Sem importações</TableCell></TableRow>
                ) : imports.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-[11.5px]">{i.filename}</TableCell>
                    <TableCell className="text-[11.5px]">{(i.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell className="text-[12.5px]">{i.imported_by_name}</TableCell>
                    <TableCell className="text-right">{i.row_count}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={i.status === "active" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-700"}>
                        {i.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canDelete && i.status === "active" && (
                        <Button size="sm" variant="ghost" onClick={() => undoImport(i.id)}>
                          <History className="h-3.5 w-3.5 mr-1" /> Desfazer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Manual entry */}
      <Dialog open={openManual} onOpenChange={setOpenManual}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Novo lançamento de hora extra</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Matrícula</Label><Input value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value })} className="font-mono" /></div>
            <div><Label>Data</Label><DateInputBR value={form.data} onChange={(v) => setForm({ ...form, data: v })} /></div>
            <div>
              <Label>Tipo de hora</Label>
              <Select value={form.tipo_hora} onValueChange={(v) => setForm({ ...form, tipo_hora: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(HOUR_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Quantidade (horas)</Label><Input type="number" step="0.01" value={form.quantidade_horas} onChange={(e) => setForm({ ...form, quantidade_horas: Number(e.target.value) })} /></div>
            <div><Label>Valor/hora (R$)</Label><Input type="number" step="0.01" value={form.valor_hora} onChange={(e) => setForm({ ...form, valor_hora: Number(e.target.value) })} /></div>
            <div><Label>Valor total (R$)</Label><Input type="number" step="0.01" value={form.valor_total} onChange={(e) => setForm({ ...form, valor_total: Number(e.target.value) })} /></div>
            <div><Label>Equipamento (código)</Label><Input value={form.equipamento_codigo || ""} onChange={(e) => setForm({ ...form, equipamento_codigo: e.target.value.toUpperCase() })} className="font-mono" /></div>
            <div><Label>Centro de custo</Label><Input value={form.centro_custo || ""} onChange={(e) => setForm({ ...form, centro_custo: e.target.value })} /></div>
            <div><Label>Função</Label><Input value={form.funcao || ""} onChange={(e) => setForm({ ...form, funcao: e.target.value })} /></div>
            <div className="md:col-span-3"><Label>Observação</Label><Textarea rows={2} value={form.observacao || ""} onChange={(e) => setForm({ ...form, observacao: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenManual(false)}>Cancelar</Button>
            <Button onClick={submitManual} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="save-overtime">Salvar lançamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import preview */}
      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Pré-visualização da importação</DialogTitle></DialogHeader>
          {importPreview && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3"><div className="text-[11px] uppercase text-emerald-700">Válidas</div><div className="text-2xl font-bold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {importPreview.valid_count}</div></div>
                <div className="bg-rose-50 border border-rose-200 rounded p-3"><div className="text-[11px] uppercase text-rose-700">Com erro</div><div className="text-2xl font-bold text-rose-800 flex items-center gap-2"><XCircle className="h-5 w-5" /> {importPreview.invalid_count}</div></div>
                <div className="bg-slate-50 border rounded p-3"><div className="text-[11px] uppercase">Total no arquivo</div><div className="text-2xl font-bold">{importPreview.total}</div></div>
              </div>
              {importPreview.invalid_count > 0 && (
                <div className="mb-3 bg-rose-50 border border-rose-200 rounded p-3 text-[12px]">
                  <div className="font-bold mb-1 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Linhas com erro (não serão importadas):</div>
                  {importPreview.invalid.slice(0, 10).map((r, i) => (
                    <div key={`err-${r.row_index}-${i}`}>Linha {r.row_index}: <span className="text-rose-700">{(r.errors || []).join(", ")}</span></div>
                  ))}
                  {importPreview.invalid.length > 10 && <div className="text-muted-foreground">…e mais {importPreview.invalid.length - 10}</div>}
                </div>
              )}
              <div className="rounded border max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>#</TableHead><TableHead>Matrícula</TableHead>
                    <TableHead>Nome</TableHead><TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Horas</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {importPreview.valid.slice(0, 50).map((r, i) => (
                      <TableRow key={`valid-${r.row_index}-${i}`}>
                        <TableCell className="font-mono text-[10.5px]">{r.row_index}</TableCell>
                        <TableCell className="font-mono text-[11px]">{r.matricula}</TableCell>
                        <TableCell className="text-[11.5px]">{r.nome_colaborador}</TableCell>
                        <TableCell className="font-mono text-[11px]">{r.data}</TableCell>
                        <TableCell><Badge variant="outline" className={TYPE_TONE[r.tipo_hora]}>{HOUR_TYPES[r.tipo_hora]}</Badge></TableCell>
                        <TableCell className="text-right">{r.quantidade_horas}</TableCell>
                        <TableCell className="text-right font-mono text-[11px]">{currency(r.valor_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DialogFooter className="mt-3">
                <Button variant="outline" onClick={() => setOpenImport(false)}>Cancelar</Button>
                <Button onClick={commitImport} disabled={!importPreview.valid_count}
                        className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="commit-import-btn">
                  Confirmar importação ({importPreview.valid_count} registros)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  const tone = {
    ochre: "bg-[#F1ECE0] border-[#C5A059]/30 text-[#5A4519]",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  }[accent] || "bg-white border-slate-200";
  return (
    <div className={`rounded-lg border p-4 ${tone}`}>
      <div className="text-[10.5px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-display mt-1 leading-tight truncate">{value}</div>
      {sub && <div className="text-[10.5px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-slate-50">
        <div className="font-display text-[13px] font-semibold flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-[#234935]" /> {title}
        </div>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}
